<template>
  <div
    class="keycap key-pos transition-[filter,transform] duration-500"
    :class="[highlighted ? 'scale-[115%] z-10' : '']"
  >
    <div
      :class="[
        `keycap-size keycap-color keycap-transitions rounded-lg p-1 duration-500`,
        `shadow`,
        small_shadow
          ? `shadow-[inset_0_-1px_1px_2px] hover:shadow-[inset_0_-1px_1px_3px]`
          : `shadow-[inset_0_-2px_1px_5px] hover:shadow-[inset_0_-2px_2px_6px]`,
        outlined ? 'keycap-outlined' : '',
      ]"
    >
      <div
        v-if="!blank"
        class="overflow-hidden w-full h-full flex justify-center items-center text-center"
      >
        <Icon
          v-if="isTrans(raw_action)"
          :="TRANS_ICON"
          class="absolute top-2 left-2 text-[0.7em] text-color-content"
        />

        <div
          v-if="
            isHoldTapInternal(action) && action.HoldTap.settings != undefined
          "
          class="absolute top-[2px] right-[9px] text-[0.7em] text-color-content tooltip"
          :class="isTrans(raw_action) ? 'opacity-30' : ''"
          data-tip="Overrides default Hold/Tap settings"
        >
          !
        </div>

        <div
          :class="[
            `p-1 leading-tight font-mono flex flex-col w-full h-full justify-center items-center text-color-content`,
            isTrans(raw_action) ? 'opacity-30' : '',
          ]"
        >
          <Icon
            v-if="isMultipleActionsInternal(action)"
            icon="fluent:more-circle-32-filled"
            class="m-auto"
          />
          <CustomAction v-else-if="isCustom(action)" :action="action.Custom" />
          <div
            v-else-if="isHoldTapInternal(action) && kc.show_hold_tap"
            class="grid grid-cols-[2fr_3fr] grid-rows-2 w-full h-full justify-center items-center text-sm"
          >
            <HoldTapInner
              :action="action.HoldTap.tap"
              variant="tap"
              :bgColor="colorContent80"
              :textColor="colorBase"
            />
            <HoldTapInner
              :action="action.HoldTap.hold"
              variant="hold"
              :bgColor="colorContent80"
              :textColor="colorBase"
            />
          </div>
          <div v-else-if="isHoldTapInternal(action)" class="m-auto text-sm">
            Hold Tap
          </div>

          <LayerAction
            v-else-if="isLayer(action) || isDefaultLayer(action)"
            :layer="isLayer(action) ? action.Layer : action.DefaultLayer"
            :bgColor="colorContent80"
            :textColor="colorBase"
            :isDefault="isDefaultLayer(action)"
          />

          <KeyCodes
            v-else
            :action="action"
            class="m-auto text-color-content-90"
            :max_length="8"
            :max_lines="3"
            :size_adjust="0"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  isTrans,
  isMultipleActionsInternal,
  isLayer,
  isDefaultLayer,
  isHoldTapInternal,
  isCustom,
} from "@/keyboard";
import {
  useKeycapColorsStore,
  useKeyboardStore,
  useKeycapStore,
  useHelpStore,
} from "@/stores";
import { generateColorSet } from "@/stores/keycapColors";
// Eslint can't see that this is used in template
// eslint-disable-next-line
import { TRANS_ICON } from "@/stores/keycap";
import KeyCodes from "@/components/keys/KeyCodes.vue";
import LayerAction from "@/components/keys/LayerAction.vue";
import HoldTapInner from "@/components/keys/HoldTapInner.vue";
import CustomAction from "@/components/keys/CustomAction.vue";

const props = defineProps<{
  // Position of key top-left corner in pixels
  x: number;
  y: number;
  // Key rotation in deg (positive is left)
  rot: number;
  // Key size in pixels
  height: number;
  width: number;
  // Position in the grid
  row: number;
  col: number;
  // Mark as currently selected / highlight by inverting color / add border
  selected?: boolean;
  highlighted?: boolean;
  outlined?: boolean;
  // Can be used to just display keycaps without legend
  blank?: boolean;
}>();

const colors = useKeycapColorsStore();
const kb = useKeyboardStore();
const kc = useKeycapStore();
const help = useHelpStore();

const color = computed(() => {
  const c = colors.at(props.row, props.col);
  return props.highlighted ? c.saturate(0.6).rotate(180) : c;
});
const set = computed(() => generateColorSet(color.value));

const colorBase = computed(() => set.value.base.toHex());
const colorContent = computed(() => set.value.content.toHex());
const colorFocus80 = computed(() => set.value.focus.alpha(0.8).toHex());
const colorFocus90 = computed(() => set.value.focus.alpha(0.9).toHex());
const colorContent80 = computed(() => set.value.content.alpha(0.8).toHex());

const colorOutline = computed(() =>
  set.value.content.mix(set.value.base, 0.3).darken(0.1).toHex()
);

const colorBg = computed(() =>
  props.selected ? colorFocus80.value : colorBase.value
);

const getActionOverride = () =>
  help.isKeySelected(props.row, props.col) ? help.overrideKeyAction : null;
const getTransActionOverride = () =>
  help.isKeySelected(props.row, props.col) ? help.overrideTransAction : null;

const avg_size = computed(() => (props.width + props.height) / 2);
const small_shadow = computed(() => avg_size.value < 50);

const raw_action = computed(() => {
  return getActionOverride() ?? kb.getAction(props.row, props.col);
});
// Resolve to final action if transparent
const action = computed(() => {
  const override = isTrans(raw_action.value)
    ? getTransActionOverride()
    : getActionOverride();
  const act = override ?? kb.getActionTransparent(props.row, props.col);
  return isTrans(act) ? "NoOp" : act;
});

// NOTE: scoped v-bind is buggy, it might be related to:
//  "requires that @vue/cli-service starts using newer vue-loader: https://github.com/vuejs/core/issues/3921"
// but seems to works fine if not using anything more complex than addition/subtraction in the v-bind directives.
// For this reason compute any complicated values here and use just the variable names in v-bind.
const keycapPosLeft = computed(() => props.x - props.width / 2 + "px");
const keycapPosRight = computed(() => props.y - props.height / 2 + "px");
const keycapFontSize = computed(() => 0.3 * props.height + "px");
</script>

<style scoped>
.key-pos {
  @apply absolute;
  left: v-bind(keycapPosLeft);
  bottom: v-bind(keycapPosRight);
}

.keycap-size {
  transform: rotate(v-bind(-rot + "deg"));
  width: v-bind(width + "px");
  height: v-bind(height + "px");
  font-size: v-bind(keycapFontSize);
}

/* bg-color hover:bg-color-focus/80 shadow-color-focus/80 hover:shadow-color-focus/90 */
/* selected ? bg-color-focus-80 */
.keycap-color {
  --tw-shadow-color: v-bind(colorFocus80);
  --tw-shadow: var(--tw-shadow-colored);
  background-color: v-bind(colorBg);
}
.keycap-color:hover {
  --tw-shadow-color: v-bind(colorFocus90);
  --tw-shadow: var(--tw-shadow-colored);
  background-color: v-bind(colorFocus80);
}

.text-color-content {
  color: v-bind(colorContent);
}
.text-color-content-90 {
  color: v-bind(colorContent);
}

.keycap-outlined {
  @apply border-[3px];
  border-color: v-bind(colorOutline);
}
</style>

<style>
.keycap-transitions {
  @apply transition-colors;
}

.keycap-no-transitions .keycap-transitions {
  transition-property: none;
}
</style>
